// Shared UI components for admin pages
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';

// Admin page header with add button
export const AdminHeader = ({ title, subtitle, onAdd, addLabel = "Add", isDialogOpen, onOpenChange, dialogTitle, children }) => (
  <div className="flex items-center justify-between mb-8">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">{title}</h1>
      <p className="text-slate-600">{subtitle}</p>
    </div>
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#5b3589] hover:bg-[#4c1d95] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" />
          {addLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Montserrat']">{dialogTitle}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  </div>
);

// Loading skeleton grid
export const LoadingGrid = ({ count = 3, cols = "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" }) => (
  <div className={`grid ${cols} gap-4`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-slate-100 animate-pulse h-48 rounded-sm" />
    ))}
  </div>
);

// Empty state with icon
export const EmptyState = ({ Icon, title, subtitle, onAdd, addLabel }) => (
  <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
    <Icon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-500 mb-4">{subtitle}</p>
    <Button onClick={onAdd} className="bg-[#5b3589] hover:bg-[#4c1d95] text-white">
      <Plus className="w-4 h-4 mr-2" />
      {addLabel}
    </Button>
  </div>
);

// Item card with image, title, status, and actions
export const ItemCard = ({ item, titleField, descField, imageField, statusField, statusBadge, Icon, onEdit, onDelete }) => (
  <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
    <div className="aspect-video bg-slate-100">
      {item[imageField] ? (
        <img src={item[imageField]} alt={item[titleField]} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-400">
          <Icon className="w-12 h-12" />
        </div>
      )}
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-900">{item[titleField]}</h3>
        {statusBadge || (statusField && (
          <span className={`text-xs px-2 py-1 rounded ${
            item[statusField] === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {item[statusField]}
          </span>
        ))}
      </div>
      <p className="text-slate-600 text-sm mb-4 line-clamp-2">{item[descField]}</p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
          <Edit className="w-3 h-3 mr-1" />Edit
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDelete(item.id)} className="text-red-600 hover:bg-red-50">
          <Trash2 className="w-3 h-3 mr-1" />Delete
        </Button>
      </div>
    </div>
  </div>
);

// Form action buttons
export const FormActions = ({ onCancel, isEditing }) => (
  <div className="flex justify-end gap-3 pt-4">
    <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
    <Button type="submit" className="bg-[#5b3589] hover:bg-[#4c1d95] text-white">
      {isEditing ? 'Update' : 'Create'}
    </Button>
  </div>
);
